/**
 * Token Error Types and Utilities
 *
 * This module provides types and helper functions to identify and handle
 * different types of errors during OKTA token refresh operations.
 *
 * Aligned with mobile implementation for consistency.
 */

import appConfig from "config/app";

import { logger } from "../logger";

/**
 * Types of possible errors during token refresh
 */
export type RefreshTokenErrorType =
  | "REFRESH_TOKEN_EXPIRED"
  | "REFRESH_TOKEN_INVALID"
  | "NETWORK_ERROR"
  | "UNKNOWN_ERROR";

/**
 * Typed error for refresh token operations
 */
export class RefreshTokenError extends Error {
  constructor(public type: RefreshTokenErrorType, message: string, public originalError?: unknown) {
    super(message);
    this.name = "RefreshTokenError";
  }
}

/**
 * Check if we're in development mode
 */
const isDev = appConfig?.app?.nodeEnv === "development";

/**
 * Parse an OKTA error to determine its type
 */
export function parseRefreshError(error: unknown): RefreshTokenErrorType {
  if (isDev) {
    logger.info(`[TokenErrors] parseRefreshError called with: ${String(error)}`);
  }

  if (error && typeof error === "object") {
    const errorObj = error as Record<string, unknown>;

    // Check for OKTA SDK error format (OAuthError)
    // OAuthError has 'errorCode', 'errorSummary', 'errorCauses' properties
    if (errorObj.errorCode || errorObj.errorSummary) {
      const errorCode = errorObj.errorCode ? String(errorObj.errorCode).toLowerCase() : "";
      const errorSummary = errorObj.errorSummary ? String(errorObj.errorSummary).toLowerCase() : "";

      if (isDev) {
        logger.info(
          `[TokenErrors] OAuthError detected: ${JSON.stringify({ errorCode, errorSummary })}`
        );
      }

      if (errorCode === "invalid_grant" || errorCode === "invalid_token") {
        if (errorSummary.includes("expired")) {
          return "REFRESH_TOKEN_EXPIRED";
        }
        return "REFRESH_TOKEN_INVALID";
      }
    }

    // Check for TokenError format (expo-auth-session style with params)
    if (errorObj.params && typeof errorObj.params === "object") {
      const params = errorObj.params as Record<string, unknown>;
      const errorCode = params.error ? String(params.error).toLowerCase() : "";
      const errorDescription = params.error_description
        ? String(params.error_description).toLowerCase()
        : "";

      if (isDev) {
        logger.info(
          `[TokenErrors] TokenError detected: ${JSON.stringify({ errorCode, errorDescription })}`
        );
      }

      if (errorCode === "invalid_grant" || errorCode === "invalid_token") {
        if (errorDescription.includes("expired")) {
          return "REFRESH_TOKEN_EXPIRED";
        }
        return "REFRESH_TOKEN_INVALID";
      }
    }

    // Check for network errors in message
    if (errorObj.message && typeof errorObj.message === "string") {
      const message = errorObj.message.toLowerCase();
      if (
        message.includes("network") ||
        message.includes("fetch") ||
        message.includes("timeout") ||
        message.includes("econnrefused") ||
        message.includes("enotfound") ||
        message.includes("err_network") ||
        message.includes("err_connection") ||
        message.includes("abort")
      ) {
        return "NETWORK_ERROR";
      }
    }

    // Check for OKTA-specific error properties
    if (errorObj.error) {
      const errorCode = String(errorObj.error).toLowerCase();

      if (errorCode === "invalid_grant" || errorCode === "invalid_token") {
        // Determine if it's expiration or invalid
        if (
          errorObj.errorDescription &&
          typeof errorObj.errorDescription === "string" &&
          errorObj.errorDescription.toLowerCase().includes("expired")
        ) {
          return "REFRESH_TOKEN_EXPIRED";
        }
        if (
          errorObj.error_description &&
          typeof errorObj.error_description === "string" &&
          errorObj.error_description.toLowerCase().includes("expired")
        ) {
          return "REFRESH_TOKEN_EXPIRED";
        }
        return "REFRESH_TOKEN_INVALID";
      }
    }

    // Check error code property
    if (errorObj.code) {
      const code = String(errorObj.code).toLowerCase();
      if (code === "invalid_grant" || code === "invalid_token") {
        return "REFRESH_TOKEN_EXPIRED";
      }
    }

    // Check for name property (AuthApiError, etc.)
    if (errorObj.name && typeof errorObj.name === "string") {
      const name = errorObj.name.toLowerCase();
      if (name.includes("auth") && errorObj.message) {
        const message = String(errorObj.message).toLowerCase();
        if (message.includes("expired") || message.includes("invalid")) {
          if (message.includes("expired")) {
            return "REFRESH_TOKEN_EXPIRED";
          }
          return "REFRESH_TOKEN_INVALID";
        }
      }
    }
  }

  // Check string errors
  if (typeof error === "string") {
    const errorStr = error.toLowerCase();
    if (errorStr.includes("expired")) {
      return "REFRESH_TOKEN_EXPIRED";
    }
    if (errorStr.includes("invalid_grant") || errorStr.includes("invalid_token")) {
      return "REFRESH_TOKEN_INVALID";
    }
    if (
      errorStr.includes("network") ||
      errorStr.includes("fetch") ||
      errorStr.includes("timeout")
    ) {
      return "NETWORK_ERROR";
    }
  }

  return "UNKNOWN_ERROR";
}

/**
 * Get error message for a given error type
 */
function getErrorMessage(errorType: RefreshTokenErrorType, error: unknown): string {
  switch (errorType) {
    case "REFRESH_TOKEN_EXPIRED":
      return "Refresh token has expired";
    case "REFRESH_TOKEN_INVALID":
      return "Refresh token is invalid or revoked";
    case "NETWORK_ERROR":
      return "Network error during token refresh";
    default:
      return `Unknown error during token refresh: ${
        error instanceof Error ? error.message : String(error)
      }`;
  }
}

/**
 * Create a RefreshTokenError from an unknown error
 */
export function createRefreshTokenError(error: unknown): RefreshTokenError {
  const errorType = parseRefreshError(error);
  const errorMessage = getErrorMessage(errorType, error);

  return new RefreshTokenError(errorType, errorMessage, error);
}

/**
 * Check if an error indicates that the refresh token is expired
 */
export function isRefreshTokenExpiredError(error: unknown): error is RefreshTokenError {
  if (error instanceof RefreshTokenError) {
    return error.type === "REFRESH_TOKEN_EXPIRED";
  }
  // Also check raw errors for backward compatibility
  return parseRefreshError(error) === "REFRESH_TOKEN_EXPIRED";
}

/**
 * Check if an error indicates that the refresh token is invalid or expired
 */
export function isRefreshTokenInvalidError(error: unknown): error is RefreshTokenError {
  if (error instanceof RefreshTokenError) {
    return error.type === "REFRESH_TOKEN_INVALID" || error.type === "REFRESH_TOKEN_EXPIRED";
  }
  // Also check raw errors
  const type = parseRefreshError(error);
  return type === "REFRESH_TOKEN_INVALID" || type === "REFRESH_TOKEN_EXPIRED";
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): error is RefreshTokenError {
  if (error instanceof RefreshTokenError) {
    return error.type === "NETWORK_ERROR";
  }
  // Also check raw errors
  return parseRefreshError(error) === "NETWORK_ERROR";
}

/**
 * Get the error type from any error
 */
export function getErrorType(error: unknown): RefreshTokenErrorType {
  if (error instanceof RefreshTokenError) {
    return error.type;
  }
  return parseRefreshError(error);
}
